import { SearchOutlined } from '@ant-design/icons';
import {
  GetBackProcedimentiIdComunicazioniParams,
  GetFrontRichiesteIdComunicazioniParams,
} from '@framework/api-client';
import {
  FormHandler,
  FormItem,
  IFormItemProps,
  TextInput,
} from '@framework/form';
import { t } from '@framework/i18n';
import { FilterButton, FlexRow, GhostCollapse } from '@framework/ui';
import { yup } from '@framework/validation';
import { Button, Divider, Space } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { useState } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useCommunicationsContext } from '../context/CommunicationsContext';
import { CommunicationExtendSearchForm } from './CommunicationExtendSearchForm';

const layout = {
  removeMargin: true,
} satisfies IFormItemProps['layout'];

export interface CommunicationSearchFormProps {
  size?: SizeType;
}

const TFormItem = FormItem as typeof FormItem<
  | GetFrontRichiesteIdComunicazioniParams
  | GetBackProcedimentiIdComunicazioniParams
>;

const CommunicationsSearchSchema = yup.object({
  oggetto: yup.string().notRequired().default(null),
  order: yup.string().notRequired().default(null),
  dataInvioDal: yup
    .string()
    .stringDate()
    .notRequired()
    .default(null)
    .label(t('communication-search-schema.sent-after', 'Inviata dopo il')),
  dataInvioAl: yup
    .string()
    .stringDate()
    .notRequired()
    .default(null)
    .label(t('communication-search-schema.sent-before', 'Inviata prima del')),
});

/**
 * Form di creazione di una comunicazione
 */
export function CommunicationSearchForm(props: CommunicationSearchFormProps) {
  const { size = 'large' } = props;
  // Pannello Filtro (Desktop)
  const [openSearchPanel, setOpenSearchPanel] = useState(false);
  const isMobile = useBreakpoint(down('md'));

  // Filtro Mobile (Drawer)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

  const { query, setQuery } = useCommunicationsContext();

  return (
    <FormHandler
      id="communication-search-form"
      layout="vertical"
      nested
      schema={CommunicationsSearchSchema}
      initialValues={query}
      onReset={() => {
        setQuery?.({
          pagina: 0,
          limite: 10,
          ordinamenti: ['-dataInvio'],
        });
      }}
      onSubmit={async (values, form) => {
        setQuery?.(values as GetFrontRichiesteIdComunicazioniParams);
      }}
    >
      <div>
        <FlexRow
          responsive
          gutter={48}
          right={
            <FilterButton
              id="filter-button-communication-search-extended"
              aria-controls="ghost-collapse-communication-search-extended"
              aria-expanded={openSearchPanel}
              size={size}
              opened={openSearchPanel}
              onClick={() => setOpenSearchPanel(!openSearchPanel)}
            >
              {t(
                'common.communication-search-form.extended-search',
                'Ricerca Estesa',
              )}
            </FilterButton>
          }
        >
          <div>
            <FlexRow
              style={{ maxWidth: 600 }}
              gutter={2}
              right={
                <Space size={4}>
                  <Button
                    icon={<SearchOutlined aria-hidden="true" />}
                    size={size}
                    type="default"
                    htmlType="submit"
                    disabled={openSearchPanel}
                  >
                    {t('common.communication-search-form.search', 'Cerca')}
                  </Button>
                </Space>
              }
            >
              <TFormItem
                name="oggetto"
                label={t(
                  'common.communication-search-form.subject',
                  'Oggetto Comunicazione',
                )}
                layout={layout}
              >
                <TextInput
                  size={size}
                  name="oggetto"
                  placeholder={t(
                    'common.communication-search-form.search-placeholder',
                    'Cerca...',
                  )}
                />
              </TFormItem>
            </FlexRow>
          </div>
        </FlexRow>
        {openSearchPanel && (
          <Divider aria-hidden="true" style={{ margin: '12px 0 0 0' }} />
        )}
        <GhostCollapse
          id="ghost-collapse-communication-search-extended"
          role="region"
          aria-labelledby="filter-button-communication-search-extended"
          noPadding
          opened={openSearchPanel}
          button={
            <Space direction={!isMobile ? 'vertical' : undefined}>
              <Button block htmlType="reset">
                {t(
                  'common.communication-search-form.remove-filters',
                  'Rimuovi Filtri',
                )}
              </Button>
              <Button
                block
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined aria-hidden="true" />}
                children={t(
                  'common.communication-search-form.search-button',
                  'Cerca',
                )}
              />
            </Space>
          }
        >
          <CommunicationExtendSearchForm size={size} />
        </GhostCollapse>
      </div>
    </FormHandler>
  );
}
